// apps/provider/src/services/api/audit.js
// Audit logging and compliance report methods
//
// Methods: createAuditLogEntry, getAuditLogs, generateControlledSubstanceReport,
//          getMonthlyControlledSubstanceLog, getDailyAuditReport, getSecurityReport,
//          getControlledSubstanceReport
//
// Response types: @prism-ehr/shared → ProviderAuditLogEntry, SecurityReport

/**
 * @returns {Object} Audit domain methods (bound to `this` of the composed apiService)
 */
export function createAuditMethods() {
  return {
    /**
     * Create an audit log entry.
     *
     * Xano: POST /erx_mobile_audit_logs
     * Xano file: 580_erx_mobile_audit_logs_POST.xs
     *
     * @param {Object} data
     * @param {string} data.userId
     * @param {string} [data.organizationId]
     * @param {string} data.eventType
     * @param {string} [data.eventDate]
     * @param {string} data.description
     * @param {string} [data.outcome]
     * @param {string} [data.prescriptionId]
     * @param {string} [data.patientId]
     * @param {string} [data.medicationId]
     * @param {string} [data.ipAddress]
     * @param {Object} [data.additionalData]
     * @returns {Promise<import('@prism-ehr/shared').ProviderAuditLogEntry>}
     */
    createAuditLogEntry(data) {
      const eventData = {
        user_id: data.userId,
        organization_id: data.organizationId || data.organization_id || null,
        event_type: data.eventType,
        event_date: data.eventDate || new Date().toISOString(),
        description: data.description,
        outcome: data.outcome || 'success',
        prescription_id: data.prescriptionId || null,
        patient_id: data.patientId || null,
        medication_id: data.medicationId || null,
        ip_address: data.ipAddress || null,
        additional_data: data.additionalData || {}
      };

      return this.request('/erx_mobile_audit_logs', {
        method: 'POST',
        body: JSON.stringify(eventData)
      });
    },

    /**
     * Get audit logs with filters.
     *
     * Xano: GET /erx_mobile_audit_logs
     * Xano file: 579_erx_mobile_audit_logs_GET.xs
     *
     * Returns paginated list with prescription addon.
     *
     * @param {Object} [filters]
     * @param {string} [filters.userId]
     * @param {string} [filters.organizationId]
     * @param {string} [filters.startDate]
     * @param {string} [filters.endDate]
     * @param {string} [filters.eventType]
     * @param {string} [filters.prescriptionId]
     * @param {string} [filters.patientId]
     * @param {string} [filters.medicationId]
     * @param {string} [filters.outcome]
     * @param {string} [filters.search]
     * @param {number} [filters.page]
     * @param {number} [filters.limit]
     * @returns {Promise<import('@prism-ehr/shared').PaginatedResponse<import('@prism-ehr/shared').ProviderAuditLogEntry>>}
     */
    getAuditLogs(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.userId) queryParams.append('user_id', filters.userId);
      if (filters.organizationId) queryParams.append('organization_id', filters.organizationId);
      if (filters.startDate) queryParams.append('start_date', filters.startDate);
      if (filters.endDate) queryParams.append('end_date', filters.endDate);
      if (filters.eventType) queryParams.append('event_type', filters.eventType);
      if (filters.prescriptionId) queryParams.append('prescription_id', filters.prescriptionId);
      if (filters.patientId) queryParams.append('patient_id', filters.patientId);
      if (filters.medicationId) queryParams.append('medication_id', filters.medicationId);
      if (filters.outcome) queryParams.append('outcome', filters.outcome);
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);

      return this.request(`/erx_mobile_audit_logs?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Generate controlled substance prescription report.
     *
     * Xano: GET /prescription_report
     * Xano file: not found in staging (endpoint may not be implemented yet)
     *
     * @param {string} userId
     * @param {string} startDate
     * @param {string} endDate
     * @param {string} [format='json']
     * @returns {Promise<Object>}
     */
    generateControlledSubstanceReport(userId, startDate, endDate, format = 'json') {
      const queryParams = new URLSearchParams();
      queryParams.append('user_id', userId);
      queryParams.append('start_date', startDate);
      queryParams.append('end_date', endDate);
      queryParams.append('format', format);
      queryParams.append('controlled_substances_only', 'true');

      return this.request(`/prescription_report?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get monthly controlled substance log.
     *
     * Xano: GET /monthly_prescription_log
     * Xano file: not found in staging (endpoint may not be implemented yet)
     *
     * @param {string} userId
     * @param {number} year
     * @param {number} month
     * @param {string} [format='json']
     * @returns {Promise<Object>}
     */
    getMonthlyControlledSubstanceLog(userId, year, month, format = 'json') {
      const queryParams = new URLSearchParams();
      queryParams.append('user_id', userId);
      queryParams.append('year', year);
      queryParams.append('month', month);
      queryParams.append('format', format);
      queryParams.append('controlled_substances_only', 'true');

      return this.request(`/monthly_prescription_log?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get daily audit report.
     *
     * Xano: GET /audit_log/daily_report
     * Xano file: not found in staging (endpoint may not be implemented yet)
     *
     * @param {string} [date] - ISO date string (defaults to today)
     * @returns {Promise<Object>}
     */
    getDailyAuditReport(date = new Date().toISOString().split('T')[0]) {
      return this.request(`/audit_log/daily_report?date=${date}`, {
        method: 'GET'
      });
    },

    /**
     * Get security report for an organization.
     *
     * Xano: GET /audit_log/security_report
     * Xano file: 595_audit_log_security_report_GET.xs
     *
     * Analyzes audit logs for security incidents: failed logins, failed MFA,
     * unusual prescription activity, permission changes, unauthorized access.
     *
     * @param {string} organizationId
     * @param {string} startDate
     * @param {string} endDate
     * @returns {Promise<import('@prism-ehr/shared').SecurityReport>}
     */
    getSecurityReport(organizationId, startDate, endDate) {
      const queryParams = new URLSearchParams();
      queryParams.append('organization_id', organizationId);
      queryParams.append('start_date', startDate);
      queryParams.append('end_date', endDate);

      return this.request(`/audit_log/security_report?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get controlled substance report with filters.
     *
     * Xano: GET /controlled_substance_report
     * Xano file: 596_controlled_substance_report_GET.xs
     *
     * Queries prescription table filtered by DEA schedule code.
     * Returns paginated list of prescription records.
     *
     * @param {import('@prism-ehr/shared').ControlledSubstanceReportFilters} [filters]
     * @returns {Promise<import('@prism-ehr/shared').PaginatedResponse<Object>>}
     */
    getControlledSubstanceReport(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.userId) queryParams.append('user_id', filters.userId);
      if (filters.organizationId) queryParams.append('organization_id', filters.organizationId);
      if (filters.startDate) queryParams.append('start_date', filters.startDate);
      if (filters.endDate) queryParams.append('end_date', filters.endDate);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.sortField) queryParams.append('sort_field', filters.sortField);
      if (filters.sortDirection) queryParams.append('sort_direction', filters.sortDirection);
      if (filters.schedule) queryParams.append('schedule', filters.schedule);

      return this.request(`/controlled_substance_report?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get audit history for a specific appointment.
     *
     * Xano: GET /appointment-audit-history/{appointment_id}
     * Xano file (staging): 1347_appointment_appointment_id_audit_history_GET.xs
     * Xano file (production): 915_appointment_appointment_id_audit_history_GET.xs
     *
     * Returns data changes and access log entries for client-side correlation.
     *
     * @param {string} appointmentId
     * @returns {Promise<{ changes: Array, access: Array }>}
     */
    getAppointmentAuditHistory(appointmentId) {
      return this.request(`/appointment-audit-history/${appointmentId}`, {
        method: 'GET'
      });
    },

    /**
     * Get audit history for a schedule override (time block or calendar note).
     *
     * Xano: GET /schedule-override-audit-history/{schedule_override_id}
     * Xano file (staging): schedule-override-audit-history_GET.xs
     * Xano file (production): schedule-override-audit-history_GET.xs
     *
     * Same shape as getAppointmentAuditHistory (creator is always null and
     * snapshot_actors empty — schedule_override has no actor columns).
     *
     * @param {number} scheduleOverrideId
     * @returns {Promise<{ changes: Array, access: Array }>}
     */
    getScheduleOverrideAuditHistory(scheduleOverrideId) {
      return this.request(`/schedule-override-audit-history/${scheduleOverrideId}`, {
        method: 'GET'
      });
    },

    /**
     * Get the insurance change history for a patient from the immutable audit trail.
     *
     * Xano: GET /patient-insurance-audit-history
     * Xano file (staging): patient-insurance-audit-history_GET.xs
     * Xano file (production): patient-insurance-audit-history_GET.xs
     *
     * Returns only audit rows where the insurance array changed, newest first,
     * as before/after snapshots. Powers the "Restore from Audit" action.
     *
     * @param {string} patientId
     * @returns {Promise<import('@prism-ehr/shared/types/api').PatientInsuranceAuditEntry[]>}
     */
    getPatientInsuranceAuditHistory(patientId) {
      return this.request(`/patient-insurance-audit-history?patient_id=${encodeURIComponent(patientId)}`, {
        method: 'GET'
      });
    },

    /**
     * Get the paginated change history for a patient record (the `patient`
     * table row only), newest first, with actors resolved server-side.
     *
     * Xano: GET /patient-audit-history
     * Xano file (staging): patient-audit-history_GET.xs
     * Xano file (production): patient-audit-history_GET.xs
     *
     * Requires the `clinical_view_patient_history` permission. No raw
     * old_data/new_data snapshots leave the server; each item carries a
     * pre-computed summary and field-level changes.
     *
     * Response is the Xano paging envelope
     * `{ items, itemsTotal, pageTotal, curPage, perPage, nextPage, prevPage }`
     * where each item is
     * `{ id, created_at, action: 'INSERT'|'UPDATE'|'DELETE', actor_name, summary, changes: [{ field, from, to, note? }] }`.
     *
     * @param {string} patientId
     * @param {{ page?: number, perPage?: number }} [options]
     * @returns {Promise<import('@prism-ehr/shared').PaginatedResponse<Object>>}
     */
    getPatientAuditHistory(patientId, { page = 1, perPage = 25 } = {}) {
      const queryParams = new URLSearchParams();
      queryParams.append('patient_id', patientId);
      queryParams.append('page', page);
      queryParams.append('per_page', perPage);

      return this.request(`/patient-audit-history?${queryParams.toString()}`, {
        method: 'GET'
      });
    },
  };
}
